import { AlertApi } from '@core/api/model/alerts/alert-api.model';
import { Alert } from '../../model/alerts/alert.model';
import { RequestResponse } from '@core/services/request/model/request.model';
import { AlertType, AlertTypeDescription } from '../../model/alerts/alert-type.enum';
import { Formula, FormulaDescription } from '../../model/alerts/alert-formula.enum';
import { OmipTerms } from '@pages/market/ui/energy-market/ui/omip/omip-filters/model/omip-filters.model';
import { AlertRequestForm } from '@pages/alerts/alert-management/models/alert-request-form.model';

export class AlertsMapper {
  static requestFromApiToDomain(input: RequestResponse<Array<AlertApi>>): RequestResponse<Array<Alert>> {
    return {
      ...input,
      data: input.data.map(AlertsMapper.fromApiToDomain),
    };
  }

  static fromApiToDomain(api: AlertApi): Alert {
    return {
      idAlertUser: api.idAlertaUser,
      type: api.tipo,
      descriptionType: api.descripcionTipo,
      startDate: api.fechaInicio,
      endDate: api.fechaFin,
      name: api.nombre,
      formula: api.formula,
      descriptionFormula: api.descripcionFormula,
      priceTarget: api.precioObjetivo,
      typeProduct: api.tipoProducto,
      cups: api.cups,
      nameInstallation: api.nombreInstalacion,
      installationCode: api.codigoInstalacion,
      cif: api.cif,
      socialReasonCode: api.codigoRazonSocial,
      socialReasonName: api.nombreRazonSocial,
      consumptionTarget: api.consumoObjetivo,
      period: api.tipoProducto ? AlertsMapper.productTypeToPeriod(api.tipoProducto) : undefined,
      targetTableColumn: AlertsMapper.setTargetTableColumn(api.descripcionTipo, api.precioObjetivo, api.consumoObjetivo),
      productTableColumn: AlertsMapper.setProductTableColumn(api.descripcionTipo, api.tipoProducto, api.nombreInstalacion),
    };
  }

  static fromDomainToApi(domain: Alert): AlertApi {
    return {
      idAlertaUser: domain.idAlertUser,
      tipo: domain.type,
      descripcionTipo: domain.descriptionType,
      fechaInicio: domain.startDate,
      fechaFin: domain.endDate,
      nombre: domain.name,
      formula: domain.formula,
      descripcionFormula: domain.descriptionFormula,
      tipoProducto: domain.typeProduct,
      precioObjetivo: domain.priceTarget,
      cups: domain.cups,
      nombreInstalacion: domain.nameInstallation,
      codigoInstalacion: domain.installationCode,
      cif: domain.cif,
      codigoRazonSocial: domain.socialReasonCode,
      nombreRazonSocial: domain.socialReasonName,
      consumoObjetivo: domain.consumptionTarget,
    };
  }

  static fromAlertToForm(alert: Alert): AlertRequestForm {
    return {
      type: alert.type,
      typeDescription: alert.descriptionType,
      name: alert.name,
      startDate: alert.startDate ? new Date(alert.startDate) : undefined,
      endDate: alert.endDate ? new Date(alert.endDate) : undefined,
      period: alert.period ?? undefined,
      formula: alert.formula ?? undefined,
      descriptionFormula: alert.descriptionFormula ?? undefined,
      typeProduct: alert.typeProduct ?? undefined,
      priceTarget: alert.priceTarget ?? undefined,
      socialReasonCode: alert.socialReasonCode ?? undefined,
      socialReasonName: alert.socialReasonName ?? undefined,
      cif: alert.cif ?? undefined,
      cups: alert.cups ?? undefined,
      nameInstallation: alert.nameInstallation ?? undefined,
      installationCode: alert.installationCode ?? undefined,
      consumptionTarget: alert.consumptionTarget ?? undefined,
    };
  }

  static productTypeToPeriod(productType: string): OmipTerms {
    const type = productType.substring(4, 5);
    const ProductDirectory: { [key: string]: OmipTerms } = {
      Y: OmipTerms.ANNUAL,
      Q: OmipTerms.QUARTERLY,
      M: OmipTerms.MONTHLY,
    };
    return ProductDirectory[type];
  }

  static descriptionToType(type: AlertTypeDescription): AlertType {
    const TypeDirectory: { [prop in AlertTypeDescription]: AlertType } = {
      [AlertTypeDescription.OMIP]: 1,
      [AlertTypeDescription.OMIE]: 2,
      [AlertTypeDescription.CONSUMO]: 3,
    };
    return TypeDirectory[type] ?? '';
  }

  static descriptionToFormula(desc: FormulaDescription): Formula {
    const FormulaDirectory: { [prop in FormulaDescription]: Formula } = {
      [FormulaDescription.MAYOR]: 1,
      [FormulaDescription.MENOR]: 2,
      [FormulaDescription.IGUAL]: 3,
      [FormulaDescription.MAYOR10]: 4,
      [FormulaDescription.MENOR10]: 5,
    };
    return FormulaDirectory[desc];
  }

  static setTargetTableColumn(type: AlertTypeDescription, priceTarget: number, consumptionTarget: number): string {
    const TargetDirectory: { [prop in AlertTypeDescription]: string } = {
      [AlertTypeDescription.OMIP]: `${priceTarget} €/MWh`,
      [AlertTypeDescription.OMIE]: `${priceTarget} €/MWh`,
      [AlertTypeDescription.CONSUMO]: `${consumptionTarget} MWh`,
    };
    return TargetDirectory[type] ?? '';
  }

  static setProductTableColumn(type: AlertTypeDescription, typeProduct: string, nameInstallation: string): string {
    const ProductDirectory: { [prop in AlertTypeDescription]: string } = {
      [AlertTypeDescription.OMIP]: typeProduct,
      [AlertTypeDescription.OMIE]: '-',
      [AlertTypeDescription.CONSUMO]: nameInstallation,
    };
    return ProductDirectory[type] ?? '';
  }
}
